import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Store, Mail, Globe, MapPin, Clock, Tag } from "lucide-react";
import LogoUpload from "@/components/LogoUpload";
import { HoursSelector } from "@/components/HoursSelector";
import { DatePopover } from "@/components/DatePopover";
import { normalizeSocialUrl, normalizeUrl } from "@/lib/urlUtils";
import { AddressFields } from "@/components/AddressFields";

const LOCATION_OPTIONS = [
  "Clintonville", "Columbus", "East Market", "German Village", "Hilliard",
  "Linden", "Reynoldsburg", "Short North", "Westerville", "Worthington",
  "Outside of the Greater Columbus Area",
];

/**
 * Shared bento-grid editor for a Market profile.
 * Used by /market-dashboard (owner) and /admin/market/:id (admin).
 */
export const MarketProfileForm = ({
  data,
  onChange,
  onSeasonYearRoundChange,
  token,
  uploadEndpoint = "/api/market/upload-logo",
  emailReadOnly = true,
  addressError = false,
  prefixSlot = null,
  suffixSlot = null,
}) => {
  const setField = (field, value) => onChange(field, value);
  const isYearRound = !!data.season_year_round;
  const isEventBased = !!data.hours_event_based;
  const [usingCustomLocation, setUsingCustomLocation] = useState(
    !!data.location && !LOCATION_OPTIONS.includes(data.location)
  );

  useEffect(() => {
    if (data.location && !LOCATION_OPTIONS.includes(data.location)) {
      setUsingCustomLocation(true);
    } else if (data.location && LOCATION_OPTIONS.includes(data.location)) {
      setUsingCustomLocation(false);
    }
  }, [data.location]);

  const handleLocationChange = (value) => {
    if (value === "Other") {
      setUsingCustomLocation(true);
      if (LOCATION_OPTIONS.includes(data.location)) setField("location", "");
      return;
    }
    setUsingCustomLocation(false);
    setField("location", value);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
      {prefixSlot}

      {/* Identity */}
      <Card className="col-span-1 md:col-span-8 bg-white rounded-xl border border-slate-100 shadow-sm card-hover" data-testid="market-identity-card">
        <CardHeader className="pb-4">
          <CardTitle className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Store className="w-5 h-5 text-slate-400" /> Market Identity
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-shrink-0 w-full md:w-auto">
              <Label className="text-slate-700 font-medium mb-2 block">Logo</Label>
              <LogoUpload
                currentLogo={data.logo}
                businessName={data.market_name}
                onUploadSuccess={(logoUrl) => {
                  setField("logo", logoUrl);
                  if (!uploadEndpoint.includes("/admin/")) setField("profile_review_needed", true);
                }}
                uploadEndpoint={uploadEndpoint}
                token={token}
              />
            </div>
            <div className="flex-1 space-y-4">
              <div>
                <Label className="text-slate-700 font-medium">Market Name</Label>
                <Input
                  value={data.market_name || ""}
                  onChange={(e) => setField("market_name", e.target.value)}
                  placeholder="Your Market Name"
                  className="h-12 mt-1 border-slate-200"
                  data-testid="market-name-input"
                />
              </div>
              <div>
                <Label className="text-slate-700 font-medium">Description</Label>
                <Textarea
                  value={data.description || ""}
                  onChange={(e) => setField("description", e.target.value)}
                  placeholder="Tell shoppers about your market..."
                  className="min-h-[100px] border-slate-200 resize-y mt-1"
                  data-testid="market-description-input"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Season */}
      <Card className="col-span-1 md:col-span-4 bg-white rounded-xl border border-slate-100 shadow-sm card-hover" data-testid="market-season-card">
        <CardHeader className="pb-4">
          <CardTitle className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Tag className="w-5 h-5 text-slate-400" /> Season
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-4 rounded-xl bg-slate-50 p-3">
            <div>
              <Label className="text-slate-700 font-medium">Year-round market</Label>
              <p className="text-xs text-slate-500 mt-1">Use this if your market runs all year.</p>
            </div>
            <Switch
              checked={isYearRound}
              onCheckedChange={(checked) => {
                if (onSeasonYearRoundChange) {
                  onSeasonYearRoundChange(checked);
                } else {
                  setField("season_year_round", checked);
                }
              }}
              data-testid="market-season-year-round-toggle"
            />
          </div>
          {!isYearRound && (
            <>
              <div>
                <Label className="text-slate-700 font-medium">Season Start</Label>
                <DatePopover value={data.season_start} onChange={(v) => setField("season_start", v)} testId="market-season-start-input" placeholder="Pick start date" />
              </div>
              <div>
                <Label className="text-slate-700 font-medium">Season End</Label>
                <DatePopover value={data.season_end} onChange={(v) => setField("season_end", v)} testId="market-season-end-input" placeholder="Pick end date" />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Contact */}
      <Card className="col-span-1 md:col-span-4 bg-white rounded-xl border border-slate-100 shadow-sm card-hover" data-testid="market-contact-card">
        <CardHeader className="pb-4">
          <CardTitle className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Mail className="w-5 h-5 text-slate-400" /> Contact Info
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-slate-700 font-medium"><Mail className="w-4 h-4 inline mr-1" /> Email</Label>
            <Input
              value={data.email || ""}
              onChange={(e) => !emailReadOnly && setField("email", e.target.value)}
              disabled={emailReadOnly}
              className={`h-12 mt-1 border-slate-200 ${emailReadOnly ? "bg-slate-50 text-slate-500" : ""}`}
              data-testid="market-email-display"
            />
          </div>
          <div>
            <Label className="text-slate-700 font-medium"><Globe className="w-4 h-4 inline mr-1" /> Website</Label>
            <Input
              type="url"
              value={data.website || ""}
              onChange={(e) => setField("website", e.target.value)}
              onBlur={(e) => setField("website", normalizeUrl(e.target.value))}
              placeholder="https://yourmarket.com"
              className="h-12 mt-1 border-slate-200"
              data-testid="market-website-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Location */}
      <Card className="col-span-1 md:col-span-4 bg-white rounded-xl border border-slate-100 shadow-sm card-hover" data-testid="market-location-card">
        <CardHeader className="pb-4">
          <CardTitle className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-slate-400" /> Location
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-slate-700 font-medium">City/Region</Label>
            <Select value={usingCustomLocation ? "Other" : data.location || ""} onValueChange={handleLocationChange}>
              <SelectTrigger className="h-12 mt-1 border-slate-200" data-testid="market-location-input">
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                {LOCATION_OPTIONS.map((location) => (
                  <SelectItem key={location} value={location}>{location}</SelectItem>
                ))}
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
            {usingCustomLocation && (
              <Input
                value={data.location || ""}
                onChange={(e) => setField("location", e.target.value)}
                placeholder="Add another city or region"
                className="h-12 mt-3 border-slate-200"
                data-testid="market-location-other-input"
              />
            )}
          </div>
          <AddressFields
            value={data.address || ""}
            onChange={(value) => setField("address", value)}
            label="Full Address"
            cityHint={data.location || "City"}
            testIdPrefix="market-address-input"
            error={addressError}
          />
        </CardContent>
      </Card>

      {/* Hours */}
      <Card className="col-span-1 md:col-span-4 bg-white rounded-xl border border-slate-100 shadow-sm card-hover" data-testid="market-hours-card">
        <CardHeader className="pb-4">
          <CardTitle className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Clock className="w-5 h-5 text-slate-400" /> Operating Hours
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-4 rounded-xl bg-slate-50 p-3">
            <div>
              <Label className="text-slate-700 font-medium">Event based</Label>
              <p className="text-xs text-slate-500 mt-1">Use individual events for dates and times.</p>
            </div>
            <Switch
              checked={isEventBased}
              onCheckedChange={(checked) => setField("hours_event_based", checked)}
              data-testid="market-hours-event-based-toggle"
            />
          </div>
          {isEventBased ? (
            <p className="text-sm text-slate-500">
              Add upcoming market dates in the My Events section at the bottom of your dashboard.
            </p>
          ) : (
            <HoursSelector value={data.hours} onChange={(schedule) => setField("hours", schedule)} />
          )}
        </CardContent>
      </Card>

      {/* Social */}
      <Card className="col-span-1 md:col-span-12 bg-white rounded-xl border border-slate-100 shadow-sm card-hover" data-testid="market-social-card">
        <CardHeader className="pb-4">
          <CardTitle className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Globe className="w-5 h-5 text-slate-400" /> Social Media
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-slate-700 font-medium flex items-center gap-2">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                Instagram
              </Label>
              <Input
                value={data.instagram || ""}
                onChange={(e) => setField("instagram", e.target.value)}
                onBlur={(e) => setField("instagram", normalizeSocialUrl(e.target.value))}
                placeholder="https://instagram.com/yourmarket"
                className="h-12 mt-1 border-slate-200"
                data-testid="market-instagram-input"
              />
            </div>
            <div>
              <Label className="text-slate-700 font-medium flex items-center gap-2">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                Facebook
              </Label>
              <Input
                value={data.facebook || ""}
                onChange={(e) => setField("facebook", e.target.value)}
                onBlur={(e) => setField("facebook", normalizeSocialUrl(e.target.value))}
                placeholder="https://facebook.com/yourmarket"
                className="h-12 mt-1 border-slate-200"
                data-testid="market-facebook-input"
              />
            </div>
            <div>
              <Label className="text-slate-700 font-medium flex items-center gap-2">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64c.3 0 .59.05.86.13V9.4a6.35 6.35 0 00-.86-.06A6.33 6.33 0 003.15 15.67a6.33 6.33 0 0010.82 4.48 6.33 6.33 0 001.86-4.48V8.73a8.28 8.28 0 004.84 1.55V6.83c-.37 0-.73-.05-1.08-.14z"/></svg>
                TikTok
              </Label>
              <Input
                value={data.tiktok || ""}
                onChange={(e) => setField("tiktok", e.target.value)}
                onBlur={(e) => setField("tiktok", normalizeSocialUrl(e.target.value))}
                placeholder="https://tiktok.com/@yourmarket"
                className="h-12 mt-1 border-slate-200"
                data-testid="market-tiktok-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {suffixSlot}
    </div>
  );
};
